import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';

interface TopBarProps {
  title: string;
  subtitle?: string;
  onMenuClick?: () => void;
}

export default function TopBar({ title, subtitle, onMenuClick }: TopBarProps) {
  const [showNotif, setShowNotif] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, role, signOut, displayName } = useAuth();
  const navigate = useNavigate();
  const notifRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const shownName = displayName || user?.email?.split('@')[0] || 'User';
  const avatarChar = shownName.charAt(0).toUpperCase();

  const {
    notifications,
    unreadNotifications,
    readIds,
    unreadCount,
    markAsRead,
    markAllAsRead,
  } = useNotifications();
  const [notifFilter, setNotifFilter] = useState<'all' | 'unread'>('all');

  const displayNotifications = notifFilter === 'all' ? notifications : unreadNotifications;

  // Đóng dropdown khi click ra ngoài
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotif(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = () => {
    setShowUserMenu(false);
    // Xóa toàn bộ localStorage trước để kill session ngay lập tức
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('sb-') || key.includes('supabase') || key.startsWith('__cache_')) {
        localStorage.removeItem(key);
      }
    });
    // Gọi signOut ở background (không chờ)
    signOut().catch(() => {});
    // Force reload trang login — session đã xóa nên sẽ hiển thị đăng nhập
    window.location.replace('/login');
  };

  return (
    <header className="h-14 md:h-16 bg-white border-b border-gray-100 flex items-center justify-between px-3 md:px-6 sticky top-0 z-20">
      <div className="flex items-center gap-3">
        {/* Hamburger - mobile only */}
        <button
          onClick={onMenuClick}
          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer lg:hidden"
        >
          <i className="ri-menu-line text-gray-600 text-xl"></i>
        </button>
        <div>
          <h1 className="text-gray-800 font-bold text-sm md:text-lg leading-tight">{title}</h1>
          {subtitle && <p className="text-gray-400 text-xs hidden sm:block">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 md:gap-3">
        {/* Search - desktop only */}
        <div className="relative hidden md:block">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            placeholder="Tìm kiếm..."
            className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#f59e0b] w-52 transition-colors"
          />
        </div>

        {/* Notification */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setShowNotif(!showNotif)}
            className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer relative"
          >
            <i className="ri-notification-3-line text-gray-600 text-base md:text-lg"></i>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {showNotif && (
            <div className="absolute right-0 top-11 w-80 bg-white rounded-xl border border-gray-100 shadow-xl overflow-hidden z-50">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <p className="font-semibold text-gray-800 text-sm">Thông báo</p>
                {unreadCount > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
                    className="text-xs text-gray-500 hover:text-[#f59e0b] font-medium cursor-pointer whitespace-nowrap"
                  >
                    Đánh dấu đã đọc
                  </button>
                )}
              </div>
              <div className="flex gap-1 px-4 pt-2 pb-1 border-b border-gray-100">
                <button
                  onClick={() => setNotifFilter('all')}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full cursor-pointer transition-colors ${notifFilter === 'all' ? 'bg-gray-100 text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  Tất cả {notifications.length > 0 && `(${notifications.length})`}
                </button>
                <button
                  onClick={() => setNotifFilter('unread')}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full cursor-pointer transition-colors ${notifFilter === 'unread' ? 'bg-gray-100 text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  Chưa đọc {unreadCount > 0 && `(${unreadCount})`}
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {displayNotifications.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-2">
                      <i className="ri-check-double-line text-gray-400 text-lg"></i>
                    </div>
                    <p className="text-gray-400 text-sm">
                      {notifFilter === 'unread' ? 'Không có thông báo chưa đọc' : 'Không có thông báo nào'}
                    </p>
                    <p className="text-gray-300 text-xs mt-1">
                      {notifFilter === 'unread' ? 'Tất cả thông báo đã được xem' : 'Tất cả khoản vay đều trong hạn'}
                    </p>
                  </div>
                ) : (
                  displayNotifications.map(notif => {
                    const isOverdue = notif.type === 'overdue';
                    const isRead = readIds.includes(notif.id);
                    return (
                      <button
                        key={notif.id}
                        onClick={() => { markAsRead(notif.id); setShowNotif(false); navigate('/loans'); }}
                        className="w-full px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors text-left border-b border-gray-50 last:border-b-0"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${isOverdue ? 'bg-red-100' : 'bg-amber-100'}`}>
                            <i className={`${isOverdue ? 'ri-alarm-warning-line' : 'ri-time-line'} ${isOverdue ? 'text-red-500' : 'text-amber-500'} text-sm`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-700 text-xs font-medium flex items-center gap-1.5">
                              {notif.loanId} — {notif.customerName} {isOverdue ? `quá hạn ${Math.abs(notif.daysLeft)} ngày` : `sắp đến hạn (${notif.daysLeft} ngày)`}
                              {!isRead && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />}
                            </p>
                            <p className="text-gray-400 text-[11px] mt-0.5">
                              Tài sản: {notif.assetName} · {notif.loanAmount.toLocaleString('vi-VN')}đ
                            </p>
                            <p className="text-gray-400 text-[11px]">
                              Hạn trả: {notif.dueDate}
                            </p>
                          </div>
                          <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full mt-0.5 ${isOverdue ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                            {isOverdue ? 'QUÁ HẠN' : 'SẮP HẾT HẠN'}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
              {notifications.length > 0 && (
                <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                  <button
                    onClick={() => { setShowNotif(false); navigate('/loans'); }}
                    className="w-full text-center text-xs text-[#f59e0b] font-medium hover:underline cursor-pointer"
                  >
                    Xem tất cả khoản vay
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Avatar + User Menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 pl-1 pr-1.5 py-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#f59e0b] flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-sm">{avatarChar}</span>
            </div>
            <i className={`ri-arrow-down-s-line text-gray-400 text-sm hidden md:block transition-transform ${showUserMenu ? 'rotate-180' : ''}`}></i>
          </button>

          {showUserMenu && (
            <div className="absolute right-0 top-12 w-64 bg-white rounded-xl border border-gray-100 shadow-xl overflow-hidden z-50">
              {/* User info */}
              <div className="px-4 py-3.5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#f59e0b] flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">{avatarChar}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{shownName}</p>
                    <p className="text-xs text-gray-400 truncate">{user?.email ?? ''}</p>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1 ${role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                      <i className={`${role === 'admin' ? 'ri-shield-star-line' : 'ri-user-line'} text-xs`}></i>
                      {role === 'admin' ? 'Quản trị viên' : 'Nhân viên'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer text-left"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-settings-3-line text-base"></i>
                  </span>
                  Cài đặt
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/settings'); setTimeout(() => { window.dispatchEvent(new CustomEvent('switch-settings-tab', { detail: 'staff' })); }, 100); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer text-left ${role !== 'admin' ? 'hidden' : ''}`}
                >
                  <span className="w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-team-line text-base"></i>
                  </span>
                  Quản lý nhân viên
                </button>
                <div className="border-t border-gray-100 my-1"></div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer text-left"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-logout-box-r-line text-base"></i>
                  </span>
                  Đăng xuất
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}